import React, { useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Box, Button, LinearProgress, MenuItem, TextField, Typography } from '@mui/material';
import RCPieChart from '../../components/charts/recharts/rcPieChart';

/** A single series definition for the pie chart. */
interface SeriesEntry {
    dataKey: string;
    name: string;
    fill: string;
}

const STATUS_SERIES: SeriesEntry[] = [
    { dataKey: 'highDropEventCount', name: 'High Drop (event)', fill: '#d32f2f' },
    { dataKey: 'stableEventCount',   name: 'Stable (event)',    fill: '#388e3c' },
];

/** A market-type option supplied by the parent page. */
interface Choice {
    id: string;
    name: string;
}

/** Market overview data item returned by `InventoryDropAnalysis/GetMarketOverview`. */
export interface MarketOverviewItem {
    marketType: string;
    name: string;
    highDropEventCount: number;
    stableEventCount: number;
    totalEventCount: number;
    venueDropCount: number;
    totalAvailableCount: number;
    highDropAvailableCount: number;
    [key: string]: unknown;
}

/** Filter state lifted to the parent page so it persists across view changes. */
interface OverviewFilters {
    hours: number;
    selectedMarketType: string;
    dropPercentage: string;
}

/** Props for {@link MarketOverviewChart}. */
interface MarketOverviewChartProps {
    /** Called when the user clicks a market card to drill down into venue detail. */
    onMarketSelect: (marketType: string) => void;
    /** Market-type options for the filter dropdown. */
    marketTypeChoices: Choice[];
    /** Shared overview data (lifted state). */
    dataOverview: MarketOverviewItem[];
    /** Setter for shared overview data. */
    setDataOverview: React.Dispatch<React.SetStateAction<MarketOverviewItem[]>>;
    /** Shared filter values (lifted state). */
    overviewFilters: OverviewFilters;
    /** Setter for shared filter values. */
    setOverviewFilters: React.Dispatch<React.SetStateAction<OverviewFilters>>;
}

/**
 * Market-overview chart panel rendered on the Venue-Level tab.
 *
 * Displays a grid of pie-chart cards — one per market type — showing the
 * ratio of high-drop to stable events.  Clicking a card triggers the
 * `onMarketSelect` callback so the parent page can switch to the venue-detail
 * grid view.
 *
 * @param props - {@link MarketOverviewChartProps}
 */
const MarketOverviewChart: React.FC<MarketOverviewChartProps> = ({
    onMarketSelect,
    marketTypeChoices,
    dataOverview,
    setDataOverview,
    overviewFilters,
    setOverviewFilters,
}) => {
    const dataProvider = useDataProvider();
    const notify       = useNotify();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Unwrap individual filter values from the shared state object
    const dropPercentage     = overviewFilters.dropPercentage;
    const setDropPercentage  = (val: string) =>
        setOverviewFilters((prev) => ({ ...prev, dropPercentage: val }));

    const selectedMarketType    = overviewFilters.selectedMarketType;
    const setSelectedMarketType = (val: string) =>
        setOverviewFilters((prev) => ({ ...prev, selectedMarketType: val }));

    const hours    = overviewFilters.hours;
    const setHours = (val: number) =>
        setOverviewFilters((prev) => ({ ...prev, hours: val }));

    const applyFilters = () => {
        setIsLoading(true);
        dataProvider
            .fetchData('InventoryDropAnalysis', `GetMarketOverview?hours=${hours}&marketType=${selectedMarketType}`)
            .then((response: { data?: unknown[] }) => {
                if (response && response.data) {
                    const mapped = (Array.isArray(response.data) ? response.data : []).map(
                        (item: unknown) => ({ ...(item as object), name: (item as MarketOverviewItem).marketType }),
                    );
                    setDataOverview([...mapped as MarketOverviewItem[]]);
                } else {
                    notify('No market overview data found.', { type: 'info', autoHideDuration: 3000 });
                }
            })
            .catch((err: Error) => {
                notify(`Failed to load market overview: ${err?.message}`, { type: 'error', autoHideDuration: 5000 });
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <Box sx={{ position: 'relative' }}>
            {isLoading && <LinearProgress color="secondary" />}
            <Box sx={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.5 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h5">Inventory Drop Status by Market Type</Typography>
                    <TextField
                        type="number"
                        inputProps={{ min: 1 }}
                        size="small"
                        value={hours}
                        onChange={(e) => setHours(Number(e.target.value))}
                        label="Drop window (h)"
                        sx={{ width: 130 }}
                    />
                    <TextField
                        select
                        size="small"
                        value={selectedMarketType}
                        onChange={(e) => setSelectedMarketType(e.target.value)}
                        label="Select Market"
                        sx={{ width: 200 }}
                    >
                        <MenuItem value="">All Markets</MenuItem>
                        {marketTypeChoices.map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.id}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        size="small"
                        value={dropPercentage}
                        onChange={(e) => setDropPercentage(e.target.value)}
                        label="Min Drop %"
                        sx={{ width: 130 }}
                    />
                    <Button variant="contained" color="primary" onClick={applyFilters}>
                        Apply Filters
                    </Button>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click a market card to view per-venue details.
                    Drop % compares current ({hours}h window) inventory vs the latest snapshot in the past 30 days.
                </Typography>

                {dataOverview.length === 0 && !isLoading && (
                    <Typography sx={{ p: 2 }}>No market overview data available.</Typography>
                )}

                {/* Chart grid */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2,1fr)',
                            md: 'repeat(3,1fr)',
                            lg: 'repeat(4,1fr)',
                        },
                        gap: 3,
                        justifyItems: 'center',
                    }}
                >
                    {(dataOverview as MarketOverviewItem[])
                        .map(item => {
                            const displayHigh = item.highDropEventCount ?? 0;
                            const totalEvent = item.totalEventCount ?? 0;

                            const highRiskPct = totalEvent > 0
                                ? (displayHigh / totalEvent) * 100
                                : 0;

                            const venueDropCount = item.venueDropCount ?? 0;
                            const totalAvailable = item.totalAvailableCount ?? 0;
                            const highDropAvailableCount = item.highDropAvailableCount ?? 0;

                            const dropPct = totalAvailable > 0
                                ? (highDropAvailableCount / totalAvailable) * 100
                                : 0;

                            return {
                                ...item,
                                displayHigh,
                                totalEvent,
                                highRiskPct,
                                venueDropCount,
                                totalAvailable,
                                highDropAvailableCount,
                                dropPct,
                            };
                        })
                        .filter(item =>
                            item.venueDropCount > 0 &&
                            item.highRiskPct >= Number(dropPercentage || 0)
                        )
                        .sort((a, b) => b.highRiskPct - a.highRiskPct)
                        .map((item) => (
                            <Box
                                key={item.marketType}
                                onClick={() => onMarketSelect?.(item.marketType)}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.85 },
                                    width: '100%',
                                    maxWidth: 430,
                                }}
                            >
                                <RCPieChart
                                    data={[item]}
                                    dataKey="name"
                                    height={220}
                                    width={400}
                                    chartSize={80}
                                    elevation={4}
                                    seriesList={STATUS_SERIES}
                                    showLegendCounts={true}
                                    legend={{
                                        layout: 'vertical',
                                        align: 'right',
                                        verticalAlign: 'middle',
                                        iconType: 'square',
                                    }}
                                    convertData={(it: MarketOverviewItem) =>
                                        STATUS_SERIES.map((s) => ({
                                            name: s.name,
                                            value: (it[s.dataKey] as number) || 0,
                                            fill: s.fill,
                                            onClick: () => onMarketSelect?.(it.marketType),
                                        }))
                                    }
                                />

                                <Box
                                    sx={{
                                        mt: 1,
                                        px: 2,
                                        py: 1,
                                        height: 100,
                                        maxHeight: 120,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'background.default',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="body2">
                                        <b>Venues with drop:</b>{' '}
                                        <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                                            {item.venueDropCount.toLocaleString()}
                                        </span>
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary">
                                        <b>Events:</b>{' '}
                                        <span style={{ fontWeight: 600 }}>
                                            {item.totalEvent.toLocaleString()}
                                        </span>
                                        {' · '}
                                        <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                                            Drop {item.displayHigh.toLocaleString()}
                                            <span style={{ fontSize: '0.8em', opacity: 0.8 }}>
                                                {' '}({item.highRiskPct.toFixed(1)}%)
                                            </span>
                                        </span>
                                        {' · '}
                                        <span style={{ color: '#388e3c' }}>
                                            Stable {item.stableEventCount.toLocaleString()}
                                        </span>
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary">
                                        <b>Listings Total:</b>{' '}
                                        <b>{item.totalAvailable.toLocaleString()}</b>
                                        {' · '}
                                        Drop{' '}
                                        <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                                            {item.highDropAvailableCount.toLocaleString()}
                                        </span>
                                        {item.dropPct !== 0 && (
                                            <>
                                                {' · '}

                                                <span
                                                    style={{
                                                        color: item.dropPct > 0 ? '#d32f2f' : '#388e3c',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {item.dropPct > 0 ? '↓' : '↑'}
                                                    {Math.abs(item.dropPct).toFixed(1)}%
                                                    {' '}(&gt;{hours}h)
                                                </span>
                                            </>
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                </Box>
            </Box>
        </Box>
    );
};

export default MarketOverviewChart;
